import { useEffect } from 'react';

const SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    body: `By accessing or using the Graphvine platform ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to all of these Terms, you may not access or use the Service. These Terms constitute a legally binding agreement between you and Graphvine.`,
  },
  {
    title: '2. Proprietary Platform & Intellectual Property',
    body: `The Graphvine platform, including all software, algorithms, data pipelines, scraping infrastructure, user interface designs, analytics methodologies, and compiled code, is the exclusive proprietary property of Graphvine. All intellectual property rights are reserved.\n\nYou may not: (a) copy, reproduce, reverse engineer, decompile, disassemble, or attempt to derive the source code of the Service; (b) use automated tools, AI assistants, or any other means to reconstruct, replicate, or create derivative works based on the Service; (c) use the Service as a reference or template to build a competing product or service; (d) scrape, extract, or systematically download data or features from the Service.`,
  },
  {
    title: '3. Confidentiality',
    body: `All data, pricing intelligence, market analytics, competitor information, and other content made available through the Service is confidential and proprietary ("Confidential Information"). You agree to:\n\n(a) Hold all Confidential Information in strict confidence;\n(b) Not disclose Confidential Information to any third party without prior written consent from Graphvine;\n(c) Use Confidential Information solely for your internal business operations and not for any competitive purpose;\n(d) Take reasonable security precautions to protect Confidential Information from unauthorized access.`,
  },
  {
    title: '4. Permitted Use',
    body: `Your account grants you a limited, non-exclusive, non-transferable license to access and use the Service solely for your own internal retail business intelligence purposes. You may not sublicense, resell, share, or provide access to any third party without explicit written authorization from Graphvine.`,
  },
  {
    title: '5. Account Security',
    body: `You are responsible for maintaining the confidentiality of your account credentials. You may not share your login credentials with any other person or entity. You must notify Graphvine immediately upon becoming aware of any unauthorized use of your account. Graphvine reserves the right to terminate accounts that have been shared or compromised.`,
  },
  {
    title: '6. Data & Privacy',
    body: `Graphvine collects session and usage data (login times, feature usage) to operate and improve the Service. This data is not sold to third parties. Pricing and market data presented in the Service is collected from publicly available retail sources and is provided for informational purposes only. Graphvine makes no warranty as to the accuracy or completeness of this data.`,
  },
  {
    title: '7. Prohibited Conduct',
    body: `You agree not to: (a) use the Service for any unlawful purpose; (b) attempt to gain unauthorized access to any portion of the Service or its infrastructure; (c) interfere with or disrupt the integrity or performance of the Service; (d) use any information obtained from the Service to contact, solicit, or conduct business with competitors in a manner that violates applicable law; (e) violate any applicable local, state, or federal cannabis regulations in connection with your use of the Service.`,
  },
  {
    title: '8. Termination',
    body: `Graphvine reserves the right to suspend or terminate your access to the Service at any time, with or without notice, for conduct that Graphvine believes violates these Terms or is harmful to Graphvine, other users, third parties, or the business interests of Graphvine. Upon termination, all licenses granted to you will immediately cease.`,
  },
  {
    title: '9. Disclaimer of Warranties',
    body: `The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. Graphvine does not warrant that the Service will be uninterrupted, error-free, or that pricing data will be complete or accurate. Your use of the Service is at your sole risk.`,
  },
  {
    title: '10. Limitation of Liability',
    body: `To the maximum extent permitted by applicable law, Graphvine shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service, even if Graphvine has been advised of the possibility of such damages.`,
  },
  {
    title: '11. Governing Law',
    body: `These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any dispute arising under these Terms shall be resolved exclusively in the state or federal courts located in California.`,
  },
  {
    title: '12. Changes to Terms',
    body: `Graphvine reserves the right to modify these Terms at any time. Continued use of the Service after any such changes constitutes your acceptance of the new Terms. It is your responsibility to review these Terms periodically.`,
  },
  {
    title: '13. Contact',
    body: `For questions about these Terms of Service, please contact Graphvine directly through your account administrator.`,
  },
];

export default function TermsOfService({ onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(2,6,23,0.92)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-2xl max-h-[85vh] flex flex-col"
        style={{ background: 'var(--g-bg)', border: '1px solid rgba(16,185,129,0.25)' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-8 py-5 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--g-border)' }}
        >
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-rajdhani text-[var(--g-text)] text-lg">
                Terms of Service
              </h2>
            </div>
            <p className="text-[var(--g-text-dim)] text-[12px] pl-6">
              Graphvine · Cannabis Retail Intelligence
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--g-text-dim)] hover:text-[var(--g-text)] transition-colors"
            aria-label="Close"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 px-8 py-6">
          <p className="text-[var(--g-text-dim)] text-[12px] mb-6 pb-4" style={{ borderBottom: '1px solid var(--g-border-faint)' }}>
            Last updated: April 27, 2026 &nbsp;·&nbsp; Effective immediately upon account creation or continued use.
          </p>

          <div className="flex flex-col gap-6">
            {SECTIONS.map((s) => (
              <div key={s.title}>
                <h3 className="font-rajdhani text-[var(--g-accent-hi)] text-sm mb-2">
                  {s.title}
                </h3>
                {s.body.split('\n\n').map((para, i) => (
                  <p key={i} className="text-[var(--g-text-dim)] text-[12px] leading-relaxed mb-2">
                    {para}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-8 py-4 flex-shrink-0"
          style={{ borderTop: '1px solid var(--g-border)' }}
        >
          <p className="text-[var(--g-text-faint)] text-[11px] ">
            © 2026 Graphvine · All Rights Reserved
          </p>
          <button
            onClick={onClose}
            className="px-5 py-2 font-rajdhani font-bold text-xs transition-opacity hover:opacity-80"
            style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid var(--g-border-hi)', color: 'var(--g-accent-hi)' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
